import {ChangeDetectionStrategy, Component, OnInit, EventEmitter, Output, Input, OnChanges, SimpleChanges} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {catchError, map, shareReplay, switchMap, take} from 'rxjs/operators';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {Location} from '@angular/common';
import {MatDialogRef} from '@angular/material/dialog';

import {GpHoldingListItemReqRes} from 'src/app/dashboard/shared/holding/GpHoldingListItem.model';
import {GpHoldingDataService} from 'src/app/services/gp/gp-holding-data.service';
import HoldingDiscriminator from 'src/app/shared/enums/HoldingDiscriminator.enum';
import {RoutingService} from 'src/app/services/routing.service';
import AccountType from 'src/app/shared/enums/AccountType.enum';
import {UnitApplicationParentType} from 'src/app/shared/enums/unit-bank-account/UnitApplicationParentType.enum';
import {UnitCreateBankAccountParams} from 'src/app/shared/models/unit-bank-account/UnitCreateBankAccountParams.model';
import {ClientBankAccountResponseBasic} from 'src/app/dashboard/models/bankAccount.model';

import {UnitCreateBankAccountModel, UnitCreateBankAccountService} from '../unit-create-bank-account.service';
import {SearchOptionsResponse} from '../../../../models/SearchOptionsResponse.model';
import {UnitBankAccountListItem} from '../../../../models/gp/UnitBankAccountListItem.model';
import {UnitCreateBankAccountComponent} from '../unit-create-bank-account.component';
import UnitApplicationStatus from 'src/app/shared/enums/unit-bank-account/UnitApplicationStatus.enum';
import {UnitBankAccountDataService} from 'src/app/services/shared/unit-bank-account-data.service';

@Component({
  selector: 'terra-unit-create-bank-account-fill-details-step',
  templateUrl: './unit-create-bank-account-fill-details-step.component.html',
  styleUrls: [
    './unit-create-bank-account-fill-details-step.component.scss',
    '../unit-create-bank-account.component.scss',
    '../../unit-shared.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnitCreateBankAccountFillDetailsStepComponent extends OnDestroyMixin implements OnInit, OnChanges {
  @Input('config') data: UnitCreateBankAccountParams;

  // consts
  HoldingDiscriminator = HoldingDiscriminator;
  UnitAccountType = AccountType;
  unitApplicationParentType = UnitApplicationParentType;
  preselectedType: UnitApplicationParentType;
  unitApplicationId: number;

  accountTypeList: AccountType[] = [];
  isOverlayOpen = false;

  @Output() dialogClosed: EventEmitter<{ reason: string, payload: any }> = new EventEmitter<{ reason: string, payload: any }>();
  pageForm: UntypedFormGroup = this.generateForm();

  private allHoldings$ = this.gpHoldingDataService.getListAll().pipe(
    map(response => response.rows || []),
    shareReplay(1)
  );

  // isPreselectedHolding$ = this.createCreBankAccountService.holdingId$.pipe(map((holdingId) => !!holdingId));

  selectedHolding$: Observable<GpHoldingListItemReqRes>;

  isSelectedHoldingAllowed$: Observable<boolean>;

  selectedHoldingHasBankAssigned$: Observable<boolean>;

  existingCreBanks$ = this.unitBankAccountDataService.getListBasic(null)
    .pipe(
      shareReplay(1),
      catchError(error => {
        return of(new SearchOptionsResponse<UnitBankAccountListItem>());
      })
    );

  selectableHoldings$ = combineLatest([this.allHoldings$, this.existingCreBanks$]).pipe(
    map(([holdings, banks]) =>
      holdings.filter(h =>
          h.unitApplicationId &&
          h.unitApplicationStatus === UnitApplicationStatus.Approved
        // && !banks.rows.some(b => b.unitApplicationId === h.unitApplicationId)
      )),
    shareReplay(1)
  );

  disabledHoldings$ = combineLatest([this.allHoldings$, this.existingCreBanks$]).pipe(
    map(([holdings, banks]) =>
      holdings.filter(h =>
          !h.unitApplicationId ||
          h.unitApplicationStatus !== UnitApplicationStatus.Approved
        // || banks.rows.some(b => b.unitApplicationId === h.unitApplicationId)
      )),
    shareReplay(1)
  );

  preselectedHolding$ = combineLatest([this.unitCreateBankAccountService.holdingId$, this.selectableHoldings$, this.disabledHoldings$]).pipe(
    map(([dialogPreselectedHoldingId, selectableHoldings, disabledHoldings]) => {
      // if (!!preselectedHoldingId) {
      //   let _preSelectedHolding = selectableHoldings.find(h => h.id === preselectedHoldingId) || null;

      //   if (!_preSelectedHolding) {
      //     _preSelectedHolding = disabledHoldings.find(h => h.id === preselectedHoldingId) || null;
      //   }

      //   return _preSelectedHolding;
      // }
      if (!!dialogPreselectedHoldingId) {
        let _preSelectedHolding = selectableHoldings.find(h => h.id === dialogPreselectedHoldingId) || null;

        if (!_preSelectedHolding) {
          _preSelectedHolding = disabledHoldings.find(h => h.id === dialogPreselectedHoldingId) || null;
        }

        return _preSelectedHolding;
      }
    }),
    shareReplay(1)
  );

  // The use of forkJoin here is to allow adding more flags that determines the data was loaded on the page.
  // Currently, it's just the holdings list.
  // dataLoaded$ = combineLatest([this.selectableHoldings$, this.isPreselectedHolding$]).pipe(shareReplay(1));
  dataLoaded$ = null;
  isPreselectedHolding$ = null;

  fees$ = this.unitCreateBankAccountService.fees$;

  isShowFees$ = new BehaviorSubject(false);

  selectedHoldingsApplicationPageUrl$: Observable<string>;

  createApplicationUrl = this.routingService.createBankApplicationPage();

  constructor(
    private unitCreateBankAccountService: UnitCreateBankAccountService,
    private fb: UntypedFormBuilder,
    private gpHoldingDataService: GpHoldingDataService,
    private routingService: RoutingService,
    private unitBankAccountDataService: UnitBankAccountDataService,
    private location: Location,
    private dialogRef: MatDialogRef<UnitCreateBankAccountComponent>
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      const params = changes.data?.currentValue as UnitCreateBankAccountParams;
      console.log(params);
      this.preselectedType = params.preselectedType as any;
      this.unitApplicationId = params.unitApplicationId;

      switch (params?.preselectedType) {
        case UnitApplicationParentType.Organization:
          this.pageForm.get('holding').setValidators(null);
          this.pageForm.get('holding').setValue(null);
          this.pageForm.get('holding').disable();
          this.pageForm.get('accountType').setValue(this.UnitAccountType.Primary);
          this.pageForm.get('accountType').disable();
          this.dataLoaded$ = of(params.attachedToId).pipe(shareReplay(1));
          this.accountTypeList = AccountType.listAll();
          break;
        case UnitApplicationParentType.Holding:
          this.pageForm.get('holding').setValidators([Validators.required]);
          this.pageForm.get('holding').enable();
          this.pageForm.get('accountType').setValue(this.UnitAccountType.Primary);
          this.pageForm.get('accountType').enable();
          this.isPreselectedHolding$ = of(params?.attachedToId).pipe(map((holdingId) => !!holdingId));
          this.dataLoaded$ = combineLatest([this.selectableHoldings$, this.isPreselectedHolding$]).pipe(shareReplay(1));
          this.accountTypeList = AccountType.listAll();
          break;
        case UnitApplicationParentType.LP:
          this.pageForm.get('holding').clearValidators();
          this.pageForm.get('holding').setValue(null);
          this.pageForm.get('holding').disable();
          this.pageForm.get('accountType').setValue(this.UnitAccountType.InvestorLlc);
          this.pageForm.get('accountType').disable();
          this.dataLoaded$ = of(params.attachedToId).pipe(shareReplay(1));
          this.accountTypeList = AccountType.listAll(false);
          break;
        default:
          this.pageForm.get('holding').setValidators([Validators.required]);
          this.pageForm.get('holding').enable();
          this.pageForm.get('accountType').enable();
          this.isPreselectedHolding$ = of(params?.attachedToId).pipe(map((holdingId) => !!holdingId));
          this.dataLoaded$ = combineLatest([this.selectableHoldings$, this.isPreselectedHolding$]).pipe(shareReplay(1));
          break;
      }

      this.pageForm.get('unitApplicationId').setValue(this.unitApplicationId);

    }
  }

  get isGpSide() {
    return this.preselectedType !== UnitApplicationParentType.LP;
  }

  get lpExternalAccount() {
    return this.routingService.lpCreateBankAccount;
  }

  ngOnInit(): void {
    // this.pageForm= this.generateForm();

    this.selectedHolding$ = this.pageForm.get('holding').valueChanges
      .pipe(
        shareReplay(1)
      );

    this.isSelectedHoldingAllowed$ = this.pageForm.get('holding').valueChanges.pipe(
      map((holding: GpHoldingListItemReqRes) => this.isHoldingHasApprovedApplication(holding)),
      shareReplay(1)
    );

    this.selectedHoldingHasBankAssigned$ = this.pageForm.get('holding').valueChanges.pipe(
      switchMap((holding: GpHoldingListItemReqRes) => this.bankExistsForHolding(holding)),
      shareReplay(1)
    );

    this.selectedHoldingsApplicationPageUrl$ = this.selectedHolding$.pipe(map(holding => this.routingService.bankApplicationPage(holding.unitApplicationId)));

    // this.handleFormValueChanges();
    if (this.preselectedType === UnitApplicationParentType.Holding) {
      this.populateFormForHolding();
    } else {
      this.populateForm();
    }
  }

  continue() {
    if (!this.pageForm.valid) {
      return;
    }

    const model = this.pageForm.getRawValue() as UnitCreateBankAccountModel;

    model.preselectedType = this.preselectedType;

    switch (this.preselectedType) {
      case UnitApplicationParentType.Holding:
        if (model.holding?.unitApplicationId && model.holding?.unitApplicationStatus === UnitApplicationStatus.Approved) {
          this.unitCreateBankAccountService.continueToReview(model);
        } else {
          const fakeBankAccount = new ClientBankAccountResponseBasic();
          fakeBankAccount.id = -1;
          fakeBankAccount.isUnitBankAccount = true;
          fakeBankAccount.nickname = model.accountNickname;
          this.dialogClosed.next({reason: 'fake', payload: fakeBankAccount});
        }
        break;
      case UnitApplicationParentType.Organization:
        // to do: check if application is approved
        this.unitCreateBankAccountService.continueToReview(model);
        break;
      case UnitApplicationParentType.LP:
        // to do: check if application is approved
        this.unitCreateBankAccountService.continueToReview(model);
        break;
      default:
        break;
    }
  }

  closeDialog() {
    this.dialogClosed.next({reason: 'external', payload: null});
  }

  holdingTrackByFn(index, item: GpHoldingListItemReqRes) {
    return item?.id;
  }

  private handleFormValueChanges() {
    this.pageForm.valueChanges.pipe(
      untilComponentDestroyed(this))
      .subscribe((formValue: UnitCreateBankAccountModel) => {
        this.showOrHideFees(formValue);
        this.unitCreateBankAccountService.updateBankAccountFormDetails(formValue);
      });
  }

  /** The form value should match the interface: CreateCreBankAccountModel */
  private generateForm(): UntypedFormGroup {
    let accountType = AccountType.None;
    switch (this.preselectedType) {
      case UnitApplicationParentType.Holding:
      case UnitApplicationParentType.Organization:
        accountType = AccountType.Primary;
        break;
      case UnitApplicationParentType.LP:
        accountType = AccountType.InvestorLlc;
        break;
      default:
        accountType = AccountType.None;
    }

    const form = this.fb.group({
      holding: null,
      accountNickname: [null, [Validators.required, Validators.maxLength(255)]],
      accountType: [accountType, Validators.required],
      unitApplicationId: null
    });
    form.valueChanges.pipe(
      untilComponentDestroyed(this))
      .subscribe((formValue: UnitCreateBankAccountModel) => {
        this.showOrHideFees(formValue);
        this.unitCreateBankAccountService.updateBankAccountFormDetails(formValue);
      });
    return form;
  }

  private populateFormForHolding() {
    combineLatest([this.unitCreateBankAccountService.bankAccountFormDetails$, this.allHoldings$, this.preselectedHolding$])
      .pipe(take(1)).subscribe(
      ([account, holdings, preselectedHolding]) => {
        const value = {
          ...account,
          holding: holdings.find(h => h.id === account?.holding?.id) || preselectedHolding
        };
        this.pageForm.patchValue(value);
        if (value.holding && value.accountType && value.accountNickname) {
          // this.isShowFees$.next(true);
          this.isShowFees$.next(false);
        }
      });
  }

  private populateForm() {
    this.unitCreateBankAccountService.bankAccountFormDetails$
      .pipe(take(1)).subscribe(
      account => {
        const value = {
          ...account,
        };
        this.pageForm.patchValue(value);
        if (value.accountType && value.accountNickname) {
          // this.isShowFees$.next(true);
          this.isShowFees$.next(false);
        }
      });
  }

  private showOrHideFees(formValue: UnitCreateBankAccountModel) {
    const isShowFees = coerceBooleanProperty(
      this.isHoldingHasApprovedApplication(formValue.holding) && formValue.accountType);
    // this.isShowFees$.next(isShowFees);
    this.isShowFees$.next(false);
  }

  private isHoldingHasApprovedApplication(holding: GpHoldingListItemReqRes) {
    return coerceBooleanProperty(holding?.unitApplicationId && holding.unitApplicationStatus === UnitApplicationStatus.Approved);
  }

  private bankExistsForHolding(holding: GpHoldingListItemReqRes) {
    return this.existingCreBanks$.pipe(
      map(b => coerceBooleanProperty(holding && b.rows.some(b => b.unitApplicationId === holding?.unitApplicationId)))
    );
  }
}
